import { INITIAL_SURVEY_FILTERS } from '@ps/survey-utils';
import { createReducer, on } from '@ngrx/store';
import { ImpersonationActions } from '@ps/auth-data-access';

import {
  SurveyListActions,
  SurveyListApiActions,
  SurveyListSocketUpdateActions,
} from '../survey-list.actions';
import {
  INITIAL_QUERY_STATE,
  INITIAL_SURVEY_PAGINATION,
} from '../../survey-state.constants';
import { SurveyQueryState } from '../../survey-state.model';
import {
  adjustStatusCounts,
  applySurveyStatusUpdateToList,
} from '../survey-status-list.helpers';
import { match } from 'ts-pattern';

const newQuery = (state: SurveyQueryState): SurveyQueryState => ({
  ...state,
  pagination: {
    ...INITIAL_SURVEY_PAGINATION,
  },
  error: null,
  loadStatus: 'loading',
});

const handleCancel = (state: SurveyQueryState): SurveyQueryState => {
  const { loadingSnapshot } = state;

  const toIdle = (
    overrides: Partial<SurveyQueryState> = {},
  ): SurveyQueryState => ({
    ...state,
    loadStatus: 'idle',
    error: null,
    loadingSnapshot: null,
    ...overrides,
  });

  return match(loadingSnapshot)
    .with({ kind: 'revert-filters' }, ({ filters, sort, pagination }) =>
      toIdle({ filters, sort, pagination }),
    )
    .with({ kind: 'revert-page' }, ({ pagination }) => toIdle({ pagination }))
    .with({ kind: 'refetch' }, () => toIdle())
    .otherwise(() => ({
      ...state,
      surveys: [],
      filters: { ...INITIAL_SURVEY_FILTERS, status: state.filters.status },
      sort: null,
      pagination: { ...INITIAL_SURVEY_PAGINATION },
      loadStatus: 'loading',
      error: null,
      loadingSnapshot: { kind: 'refetch' },
    }));
};

// A load that fails or times out must stop advertising more pages. The
// infinite-scroll sentinel chains a new loadNextPage on every loading-cycle
// completion while it stays on screen, so leaving hasMore=true after a
// persistent error (e.g. CORS, which arrives here as a timeout) loops requests
// endlessly. The next user query resets hasMore via newQuery/INITIAL state.
const stopPaging = (state: SurveyQueryState): SurveyQueryState => ({
  ...state,
  pagination: { ...state.pagination, hasMore: false },
});

export const surveyQueryReducer = createReducer(
  INITIAL_QUERY_STATE,

  on(SurveyListActions.init, (state, { context }) => ({
    ...state,
    ...INITIAL_QUERY_STATE,
    context: { ...context },
  })),

  // Inverse of init: clearing the loaded list drops selectSurveyIds to [], which
  // is what drives SurveySocketEffects.syncSurveyRooms$ to leave the room.
  on(SurveyListActions.teardown, (state) => ({
    ...state,
    ...INITIAL_QUERY_STATE,
  })),

  on(SurveyListActions.setFilters, (state, { filters }) => ({
    ...state,
    ...newQuery(state),
    filters: { ...state.filters, ...filters },
    loadingSnapshot: {
      kind: 'revert-filters',
      filters: state.filters,
      sort: state.sort,
      pagination: state.pagination,
    },
  })),

  on(SurveyListActions.setSort, (state, { sort }) => ({
    ...state,
    ...newQuery(state),
    sort: { ...sort },
    loadingSnapshot: {
      kind: 'revert-filters',
      filters: state.filters,
      sort: state.sort,
      pagination: state.pagination,
    },
  })),

  on(SurveyListActions.setTab, (state, { tabId, status }) => ({
    ...state,
    ...newQuery(state),
    activeTabId: tabId,
    surveys: [],
    filters: { ...state.filters, status },
    loadingSnapshot: null,
  })),

  on(SurveyListActions.loadNextPage, (state) => {
    if (state.loadStatus !== 'idle') {
      return state;
    }

    return {
      ...state,
      pagination: { ...state.pagination, page: state.pagination.page + 1 },
      loadStatus: 'loading',
      error: null,
      loadingSnapshot: {
        kind: 'revert-page',
        pagination: state.pagination,
      },
    };
  }),

  on(SurveyListActions.cancelLoad, handleCancel),
  on(SurveyListApiActions.loadTimeout, (state) =>
    stopPaging(handleCancel(state)),
  ),

  on(SurveyListApiActions.slowLoadingDetected, (state) => ({
    ...state,
    loadStatus: 'slow-loading',
  })),

  on(SurveyListApiActions.loadSuccess, (state, { surveys = [] }) => {
    // For page 1, replace entirely. For subsequent pages, append while
    // deduplicating: some sort fields (e.g. countryCode, fielded, trafficChannels)
    // have many duplicate values, making the server cursor unstable — the same
    // survey can appear on both page 1 and page 2. Without deduplication, a
    // "select all" on page 1 causes those duplicates to render as selected when
    // page 2 loads.
    const existing = state.pagination.page === 1 ? [] : state.surveys;
    const existingIds = new Set(existing.map((s) => s.id));
    return {
      ...state,
      surveys: [...existing, ...surveys.filter((s) => !existingIds.has(s.id))],
      pagination: {
        ...state.pagination,
        hasMore: surveys.length === state.pagination.limit,
      },
      loadStatus: 'idle',
      error: null,
      loadingSnapshot: null,
    };
  }),

  on(SurveyListApiActions.loadFailure, (state, { error }) =>
    stopPaging({
      ...state,
      error,
      loadStatus: 'idle',
      loadingSnapshot: null,
    }),
  ),

  on(SurveyListApiActions.loadCountsSuccess, (state, { counts }) => ({
    ...state,
    counts,
  })),

  on(
    SurveyListApiActions.updateSurveyStatusSuccess,
    (state, { surveyId, status, statusFilter }) => {
      const previousStatus = state.surveys.find(
        (survey) => survey.id === surveyId,
      )?.status;

      return {
        ...state,
        surveys: applySurveyStatusUpdateToList(
          state.surveys,
          surveyId,
          status,
          statusFilter,
        ),
        counts: adjustStatusCounts(state.counts, previousStatus, status),
      };
    },
  ),

  on(SurveyListApiActions.updateSurveyStatusFailure, (state, { error }) => ({
    ...state,
    error,
    loadStatus: 'idle',
  })),

  on(
    SurveyListSocketUpdateActions.updateSurvey,
    (state, { surveyId, survey }) => ({
      ...state,
      surveys: state.surveys.map((current) =>
        current.id === surveyId ? { ...current, ...survey } : current,
      ),
    }),
  ),

  on(
    SurveyListSocketUpdateActions.filterSurveyByStatus,
    (state, { surveyId, newStatus, oldStatus, isAllTab }) => {
      const surveys = isAllTab
        ? state.surveys.map((survey) =>
            survey.id === surveyId ? { ...survey, status: newStatus } : survey,
          )
        : state.surveys.filter((survey) => survey.id !== surveyId);

      return {
        ...state,
        surveys,
        counts: adjustStatusCounts(state.counts, oldStatus, newStatus),
      };
    },
  ),

  on(
    ImpersonationActions.contextSettled,
    (state, { hasImpersonatedBuyer, companyId }): SurveyQueryState => ({
      ...INITIAL_QUERY_STATE,
      context: state.context && { ...state.context, hasImpersonatedBuyer },
      filters: {
        ...INITIAL_QUERY_STATE.filters,
        ...(hasImpersonatedBuyer && { buyerId: companyId }),
      },
    }),
  ),
);
